/* eslint-disable complexity */
import React, { useContext, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { workspaceByIdSelector } from '@futrli/shared-services';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';
import { UILoader } from '@futrli/ui-components';

import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { StoreState, VIEW_TYPES } from '../../../../core/types';
import { CATEGORY_NAME_INDEXES, REPORT_SECTION } from '../../../../core/constants';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';
import { ProfitLossPredictionsPageWrapper } from '../profit-loss-predictions-page-wrapper';
import { getConsolidatedGridInfo } from '../util-fns/consolidation-util-fns';
import { useConsolidationDataContext } from '../../../../predict-management/context/consolidation-data-context';
import { ForecastUpdating } from '../../components/forecast-updating';

export const ConsolidationSales = () => {
  const intl = useIntl();
  const { workspaceId: tempWorkspaceId } = useParams();
  const workspaceId = tempWorkspaceId || '';

  const {
    consolidatedForecastReport,
    forecast,
    isLoading,
    periodType,
    setViewType,
    totalCashPredictRow,
    viewType
  } = usePredictionsInfoContext();

  const workspace = useSelector((state: StoreState) => workspaceByIdSelector(state.normalised, workspaceId));

  const { consolidation } = useConsolidationDataContext();

  const { nodeStaticIds } = useLocalisedConstantsContext();

  const { expandedRows, rowHeights, getShowZeroValueAccounts } = useContext(UserGridSettingsContext);

  useEffect(() => {
    if (!viewType) {
      setViewType?.(VIEW_TYPES.ACCRUALS_PL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType]);

  const viewOptions = useMemo(() => {
    return [
      {
        name: intl.formatMessage({
          id: 'predict.accruals_pl_view',
          defaultMessage: 'Accruals (P&L) view'
        }),
        value: VIEW_TYPES.ACCRUALS_PL
      },
      {
        name: intl.formatMessage({
          id: 'predict.cash_flow_view',
          defaultMessage: 'Cash flow view'
        }),
        value: VIEW_TYPES.CASH_FLOW
      }
    ];
  }, [intl]);

  const { dataGridRows, totalRow } = useMemo(() => {
    return getConsolidatedGridInfo(
      intl,
      nodeStaticIds,
      viewType === VIEW_TYPES.CASH_FLOW ? REPORT_SECTION.CashFlow : REPORT_SECTION.ProfitLoss,
      CATEGORY_NAME_INDEXES.income,
      workspace,
      expandedRows,
      periodType,
      consolidatedForecastReport,
      consolidation,
      getShowZeroValueAccounts(forecast?.id || '')
    );
  }, [
    intl,
    nodeStaticIds,
    viewType,
    workspace,
    expandedRows,
    periodType,
    consolidatedForecastReport,
    consolidation,
    getShowZeroValueAccounts,
    forecast?.id
  ]);

  if (!consolidatedForecastReport && isLoading) {
    return <UILoader isFull />;
  }

  if (!dataGridRows || !consolidatedForecastReport || !workspace || !consolidation) {
    return null;
  }

  return (
    <>
      <ProfitLossPredictionsPageWrapper
        reportColumns={
          viewType === VIEW_TYPES.CASH_FLOW
            ? consolidatedForecastReport.data.cash_flow_columns
            : consolidatedForecastReport.data.columns || []
        }
        rowHeights={rowHeights}
        category={CATEGORY_NAME_INDEXES.income}
        viewOptions={viewOptions}
        accountRows={dataGridRows}
        totalRow={totalRow}
        totalCashPredictRow={totalCashPredictRow}
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
